import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateFormDTO,
  UpdateFormDTO,
} from "@/modules/form-center/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { formsApi } from "./client";

export const formsKeys = {
  all: ["forms"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["forms", "list", q] as const,
  detail: (id: string) => ["forms", "detail", id] as const,
  submissions: (formId: string, q: Partial<OffsetPageQueryDTO>) =>
    ["forms", "submissions", formId, q] as const,
  submissionDetail: (formId: string, submissionId: string) =>
    ["forms", "submission-detail", formId, submissionId] as const,
  fields: (formId: string, versionId: string) =>
    ["forms", "fields", formId, versionId] as const,
  versions: (formId: string) => ["forms", "versions", formId] as const,
  analyticsStats: (formId: string) =>
    ["forms", "analytics", "stats", formId] as const,
  analyticsTrends: (formId: string, period: string) =>
    ["forms", "analytics", "trends", formId, period] as const,
  categories: ["forms", "categories"] as const,
};

export function useFormsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: formsKeys.list(q),
    queryFn: () => formsApi.list(q),
  });
}

export function useFormQuery(id: string) {
  return useQuery({
    queryKey: formsKeys.detail(id),
    queryFn: () => formsApi.get(id),
    enabled: !!id,
  });
}

export function useFormSubmissionsQuery(
  formId: string,
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: formsKeys.submissions(formId, q),
    queryFn: () => formsApi.listSubmissions(formId, q),
    enabled: !!formId,
  });
}

export function useFormSubmissionDetailQuery(
  formId: string,
  submissionId: string,
) {
  return useQuery({
    queryKey: formsKeys.submissionDetail(formId, submissionId),
    queryFn: () => formsApi.getSubmissionDetail(formId, submissionId),
    enabled: !!formId && !!submissionId,
  });
}

export function useCreateForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFormDTO) => formsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງຟອມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ສ້າງຟອມລົ້ມເຫຼວ"),
  });
}

export function useUpdateForm(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateFormDTO) => formsApi.update(id, input),
    onSuccess: () => {
      toast.success("ແກ້ໄຂຟອມສໍາເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ແກ້ໄຂຟອມລົ້ມເຫຼວ"),
  });
}

export function useDeleteForm() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => formsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

export function useFormFieldsQuery(formId: string, versionId: string | null) {
  return useQuery({
    queryKey: formsKeys.fields(formId, versionId ?? ""),
    queryFn: () => formsApi.getFieldsByVersion(formId, versionId ?? ""),
    enabled: !!formId && !!versionId,
  });
}

export function useFormVersionsQuery(formId: string) {
  return useQuery({
    queryKey: formsKeys.versions(formId),
    queryFn: () => formsApi.getVersions(formId),
    enabled: !!formId,
  });
}

export function useDuplicateForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsApi.duplicate(id),
    onSuccess: () => {
      toast.success("ສຳເນົາຟອມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ສຳເນົາຟອມລົ້ມເຫຼວ"),
  });
}

export function useFormAnalyticsStats(formId: string) {
  return useQuery({
    queryKey: formsKeys.analyticsStats(formId),
    queryFn: () => formsApi.getAnalyticsStats(formId),
    enabled: !!formId,
  });
}

export function useFormSubmissionTrends(
  formId: string,
  period: "7d" | "30d" | "90d" | "1y" = "30d",
) {
  return useQuery({
    queryKey: formsKeys.analyticsTrends(formId, period),
    queryFn: () => formsApi.getSubmissionTrends(formId, period),
    enabled: !!formId,
  });
}

export function useFormCategories() {
  return useQuery({
    queryKey: formsKeys.categories,
    queryFn: () => formsApi.getCategories(),
  });
}

export function useFormCategoryLookup(params?: {
  q?: string;
  limit?: number;
  skip?: number;
}) {
  return useQuery({
    queryKey: ["forms", "categories", "lookup", params],
    queryFn: () => formsApi.getCategoryLookup(params),
  });
}
